import React, { useState, useEffect } from 'react';
import { CloudRain, Sun, Wind, Cloud, ThermometerSun, Leaf, AlertCircle } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useLocation } from '@/lib/locations';
import { Button } from '@/components/ui/button';

export const WeatherRecommendation = () => {
  const location = useLocation();
  const [weather, setWeather] = useState<{ temp: number, condition: string, recommendation: string, aqi: number, aqiStatus: string } | null>(null);

  useEffect(() => {
    // Simulate fetching local weather based on location
    const fetchWeather = async () => {
      const isHot = location.regionSlug === 'phoenix-area';
      const isCoastal = ['san-diego', 'orange-county', 'la-south-bay', 'san-francisco'].includes(location.regionSlug);
      
      let condition = 'Sunny';
      let temp = 75;
      let rec = 'Perfect day for a Standard Clean to keep the dust at bay and maintain a fresh home environment.';
      let aqi = 42;
      let aqiStatus = 'Good';
      
      if (isHot) {
        temp = 95;
        condition = 'Hot';
        rec = 'High temperatures mean more AC use. Great time for a Deep Clean to remove circulated dust from vents and fans.';
        aqi = 65;
        aqiStatus = 'Moderate';
      } else if (isCoastal) {
        temp = 68;
        condition = 'Breezy';
        rec = 'Coastal breeze can bring in salt and sand. We highly recommend adding window and blind cleaning to your package today!';
        aqi = 35;
        aqiStatus = 'Excellent';
      } else {
        // Randomize slightly for other regions to feel dynamic
        const rand = Math.random();
        if (rand > 0.6) {
          condition = 'High Pollen';
          rec = 'Allergy season is here. We recommend a Deep Clean to remove allergens, pollen, and pet dander from your home.';
          aqi = 85;
          aqiStatus = 'Fair';
        }
      }

      setWeather({ temp, condition, recommendation: rec, aqi, aqiStatus });
    };

    fetchWeather();
  }, [location.regionSlug]);

  if (!weather) return null;

  const isWarning = weather.aqi > 60 || weather.condition === 'High Pollen';

  return (
    <Card className="bg-gradient-to-br from-card to-muted border-border shadow-md overflow-hidden relative group">
      <div className="absolute -right-8 -top-8 opacity-[0.03] group-hover:scale-110 transition-transform duration-700">
        {weather.condition === 'Sunny' || weather.condition === 'Hot' ? <Sun size={200} /> : 
         weather.condition === 'Breezy' ? <Wind size={200} /> : 
         weather.condition === 'High Pollen' ? <Leaf size={200} /> : <Cloud size={200} />}
      </div>
      
      <CardContent className="p-0">
        <div className="flex flex-col md:flex-row">
          {/* Left Side - Weather Stats */}
          <div className="p-6 md:w-1/3 bg-primary/5 flex flex-col justify-center border-b md:border-b-0 md:border-r border-border relative overflow-hidden">
            <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-accent"></div>
            <div className="flex items-center justify-between mb-4">
              <div className="bg-background p-3 rounded-2xl shadow-sm text-primary relative">
                {weather.condition === 'Sunny' ? <Sun className="w-8 h-8 animate-[spin_10s_linear_infinite]" /> : 
                 weather.condition === 'Hot' ? <ThermometerSun className="w-8 h-8 animate-pulse text-orange-500" /> : 
                 weather.condition === 'High Pollen' ? <Leaf className="w-8 h-8 text-green-500 animate-pulse" /> :
                 weather.condition === 'Breezy' ? <Wind className="w-8 h-8 animate-pulse" /> : <CloudRain className="w-8 h-8" />}
              </div>
              <div className="text-4xl font-black tracking-tighter text-foreground">
                {weather.temp}°
              </div>
            </div>
            
            <h3 className="font-bold text-lg leading-tight mb-1">{location.name}</h3>
            <p className="text-sm text-muted-foreground font-medium">{weather.condition} conditions</p>
            
            <div className="mt-4 pt-4 border-t border-border/50 flex items-center justify-between">
              <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wider">Air Quality</span>
              <Badge variant={isWarning ? "destructive" : "secondary"} className={isWarning ? "bg-orange-500/10 text-orange-600 border-orange-500/20 hover:bg-orange-500/20" : "bg-green-500/10 text-green-600 border-green-500/20 hover:bg-green-500/20"}>
                {weather.aqi} AQI - {weather.aqiStatus}
              </Badge>
            </div>
          </div>
          
          {/* Right Side - Recommendation */}
          <div className="p-6 md:w-2/3 flex flex-col justify-center relative">
            <div className="flex items-center gap-2 mb-3">
              <AlertCircle className="w-5 h-5 text-primary" />
              <h4 className="font-bold text-foreground">Smart Cleaning Forecast</h4>
            </div>
            
            <p className="text-muted-foreground leading-relaxed mb-6">
              {weather.recommendation}
            </p>
            
            <div className="mt-auto flex items-center justify-between">
              <Button size="sm" asChild className="rounded-full px-6 shadow-elegant transition-transform hover:-translate-y-0.5">
                <a href="/book">Secure Your Booking</a>
              </Button>
              <span className="text-xs text-muted-foreground font-medium flex items-center gap-1.5">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500"></span>
                </span>
                Live data active
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};